import {
  FormControl,
  Input,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { ChangeEvent, ReactNodeArray, useMemo } from 'react';
import { JupiterTheme } from '../theme/types';
import { IntegrationDefinition, IntegrationInstance } from './types';
import useIntegrationInstancesAndDefinitions from './useIntegrationInstancesAndDefinitions';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    formControl: { minWidth: 240, marginBottom: theme.spacing(1) },
    subHeader: {
      color: theme.palette.common.black,
      borderBottom: `1px solid ${theme.palette.coolGrey[500]}`,
      lineHeight: 0,
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(1.5),
    },
  })
);

interface Props {
  value?: string;
  integrationFilter?: string;
  onSelect: (instanceId: string) => void;
  disabled?: boolean;
}

interface IntegrationMap {
  [definitionName: string]: IntegrationInstance[];
}
const groupInstances = (
  instances: IntegrationInstance[],
  definitions: IntegrationDefinition[],
  definitionFilter?: string
) =>
  definitions
    .filter((d) =>
      definitionFilter ? d.name.indexOf(definitionFilter) >= 0 : true
    )
    .reduce((acc: IntegrationMap, d) => {
      const groupedInstances = instances.filter(
        (i) => i.integrationDefinitionId === d.id
      );
      if (groupedInstances.length) {
        acc[d.name] = groupedInstances;
      }
      return acc;
    }, {});

const getIntegrationInstanceGroup = (
  definitionName: string,
  instances: IntegrationInstance[],
  classes: any
) => {
  return [
    <ListSubheader
      key={definitionName}
      className={classes.subHeader}
      disableSticky={true}
    >
      {definitionName}
    </ListSubheader>,
    ...instances.map((i) => (
      <MenuItem value={i.id} key={i.id}>
        {i.name}
      </MenuItem>
    )),
  ];
};

const IntegrationInstanceSelector = ({
  value,
  onSelect,
  disabled,
  integrationFilter,
}: Props) => {
  const { instances, definitions } = useIntegrationInstancesAndDefinitions();
  const classes = useStyles();

  const groupedInstances = useMemo(
    () =>
      instances && definitions
        ? groupInstances(instances, definitions, integrationFilter)
        : ({} as IntegrationMap),
    [integrationFilter, instances, definitions]
  );

  const handleSelect = (e: ChangeEvent<{ value: unknown }>) =>
    onSelect(e.target.value as string);

  const menuItems = useMemo(
    () =>
      Object.keys(groupedInstances).reduce(
        (acc, k) => [
          ...acc,
          ...getIntegrationInstanceGroup(k, groupedInstances[k], classes),
        ],
        [] as ReactNodeArray
      ),
    [groupedInstances]
  );

  return (
    <FormControl className={classes.formControl}>
      <InputLabel>Integration Instance</InputLabel>
      <Select
        onChange={handleSelect}
        value={value || undefined}
        input={<Input />}
        disabled={disabled}
      >
        <MenuItem value={undefined}>
          <em>-----</em>
        </MenuItem>
        {menuItems}
      </Select>
    </FormControl>
  );
};

export default IntegrationInstanceSelector;
